import { StatsigClient } from '@statsig/js-client';

/**
 * Statsig singleton module.
 * Fetches gate/experiment values directly from Statsig servers.
 */
class StatsigManager {
  constructor() {
    this.client = null;
    this.initPromise = null;
  }

  /**
   * Initialize the Statsig client.
   * Fetches values from Statsig servers.
   * @returns {Promise<void>}
   */
  async initialize() {
    if (!this.initPromise) {
      this.initPromise = this._doInitialize();
    }
    return this.initPromise;
  }

  async _doInitialize() {
    const data = window.statsigData;

    // Check if client key exists
    if (!data?.enabled || !data?.clientKey) {
      return;
    }

    try {
      this.client = new StatsigClient(data.clientKey, data.user);
      await this.client.initializeAsync();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Statsig initialization error:', e);
      this.client = null;
    }
  }

  /**
   * Check a feature gate.
   * @param {string} gateName - Name of the gate.
   * @returns {boolean} True if gate is enabled.
   */
  checkGate(gateName) {
    if (!this.client) {
      return false;
    }
    try {
      return this.client.checkGate(gateName);
    } catch {
      return false;
    }
  }

  /**
   * Get experiment configuration.
   * @param {string} experimentName - Name of the experiment.
   * @returns {{ groupName: string|null, value: object }} Experiment data.
   */
  getExperiment(experimentName) {
    if (!this.client) {
      return { groupName: null, value: {} };
    }
    try {
      const experiment = this.client.getExperiment(experimentName);
      return {
        groupName: experiment.getGroupName?.() ?? null,
        value: experiment.getValue() ?? {},
      };
    } catch {
      return { groupName: null, value: {} };
    }
  }

  /**
   * Get dynamic config.
   * @param {string} configName - Name of the config.
   * @returns {object} Config values.
   */
  getDynamicConfig(configName) {
    if (!this.client) {
      return {};
    }
    try {
      const config = this.client.getDynamicConfig(configName);
      return config.getValue() ?? {};
    } catch {
      return {};
    }
  }

  /**
   * Log an event to Statsig.
   * @param {string} eventName - Name of the event.
   * @param {string|number|null} value - Optional event value.
   * @param {object} metadata - Optional event metadata.
   */
  logEvent(eventName, value = null, metadata = {}) {
    if (!this.client) {
      return;
    }
    try {
      this.client.logEvent(eventName, value, metadata);
    } catch {
      // Silently fail - analytics loss is acceptable
    }
  }
}

// Export singleton instance
const statsig = new StatsigManager();
export default statsig;
